import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Settings, Save, RefreshCw, AlertTriangle, Banknote, Video, Wifi } from "lucide-react";

const CHANNELS = [
  { key: "efectivo_bbva", label: "Efectivo BBVA" },
  { key: "tarjeta_clip", label: "Tarjeta CLIP" },
  { key: "rappi", label: "Rappi" },
  { key: "uber_eats", label: "Uber Eats" },
  { key: "didi", label: "DiDi" },
  { key: "oxxo_pay", label: "Oxxo Pay" },
];

const TRAINING_VIDEO_ROLES = [
  { key: "corporate_admin", label: "Admin Corporativo" },
  { key: "branch_manager", label: "Gerente de Sucursal" },
  { key: "operativo", label: "Operativo" },
  { key: "treasury", label: "Finanzas" },
  { key: "supplier_welcome", label: "Bienvenida Proveedor (video guía)" },
];

export default function OpsAdminConfig() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const [wansoftStatus, setWansoftStatus] = useState(null);
  const [wansoftRuns, setWansoftRuns] = useState([]);
  const [wansoftCookie, setWansoftCookie] = useState("");
  const [wansoftSaving, setWansoftSaving] = useState(false);
  const [wansoftSyncing, setWansoftSyncing] = useState(false);
  const [wansoftError, setWansoftError] = useState("");
  const [wansoftMsg, setWansoftMsg] = useState("");
  const [wansoftFrom, setWansoftFrom] = useState("");
  const [wansoftTo, setWansoftTo] = useState("");
  const [wansoftHelpOpen, setWansoftHelpOpen] = useState(false);

  const isCorpAdmin = user?.role === "corporate_admin";

  useEffect(() => {
    Promise.all([
      api.get("/ops/config"),
      api.get("/branches"),
    ]).then(([cfgRes, brRes]) => {
      setConfig(cfgRes.data);
      setBranches((brRes.data || []).filter(b => !b.is_corporate));
    }).catch(e => setError("Error al cargar la configuracion.")).finally(() => setLoading(false));
  }, []);

  const fetchWansoft = useCallback(() => {
    if (!isCorpAdmin) return;
    Promise.all([
      api.get("/ops/wansoft/session-status"),
      api.get("/ops/wansoft/sync-runs?limit=8"),
    ]).then(([st, runs]) => {
      setWansoftStatus(st.data);
      setWansoftRuns(runs.data?.runs || []);
    }).catch(() => {});
  }, [isCorpAdmin]);

  useEffect(() => { fetchWansoft(); }, [fetchWansoft]);

  const handleWansoftSync = async () => {
    setWansoftSyncing(true); setWansoftError(""); setWansoftMsg("");
    try {
      const params = {};
      if (wansoftFrom) params.from_date = wansoftFrom;
      if (wansoftTo) params.to_date = wansoftTo;
      await api.post("/ops/wansoft/sync-sales", null, { params });
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const r = await api.get("/ops/wansoft/sync-runs/last");
          const run = r.data?.run;
          if (run && run.estado === "corriendo" && tries < 40) {
            setTimeout(poll, 5000);
          } else {
            setWansoftSyncing(false);
            fetchWansoft();
          }
        } catch { setWansoftSyncing(false); }
      };
      setTimeout(poll, 5000);
    } catch (e) {
      setWansoftError(e?.response?.data?.detail || "Error al iniciar la sincronizacion.");
      setWansoftSyncing(false);
    }
  };

  const handleSaveWansoftCookie = async () => {
    if (!wansoftCookie.trim()) return;
    setWansoftSaving(true); setWansoftError(""); setWansoftMsg("");
    try {
      const res = await api.put("/ops/wansoft/aspxauth", { aspxauth: wansoftCookie.trim() });
      setWansoftStatus(res.data);
      setWansoftCookie("");
      setWansoftMsg(res.data.validated_alive
        ? "Cookie guardada y validada: la sesion esta activa."
        : "Cookie guardada, pero no se pudo confirmar de inmediato que este activa.");
      fetchWansoft();
    } catch (e) {
      setWansoftError(e?.response?.data?.detail || "Error al guardar la cookie.");
    } finally { setWansoftSaving(false); }
  };

  const set = (path, val) => {
    setConfig(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let obj = updated;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = val;
      return updated;
    });
  };

  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      await api.put("/ops/config", {
        reconciliation_windows: config.reconciliation_windows,
        reconciliation_thresholds: config.reconciliation_thresholds,
        alert_anticipation_days: config.alert_anticipation_days,
        viaticos_policy: config.viaticos_policy,
        alert_channels: config.alert_channels,
        saldo_minimo_default: config.saldo_minimo_default,
        saldo_minimo_por_sucursal: config.saldo_minimo_por_sucursal,
        training_video_urls: config.training_video_urls,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al guardar.");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 flex justify-center"><RefreshCw className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (!config) return <div className="p-6 text-slate-500">No se pudo cargar la configuracion.</div>;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="ops-admin-config-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Settings className="w-6 h-6 text-yellow-500" /> Configuracion Ops
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">Ventanas de conciliacion, umbrales, politica de viaticos y alertas</p>
        </div>
        <button onClick={handleSave} disabled={saving}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${saved ? "bg-green-500 text-white" : "bg-[#FFDF00] text-slate-900 hover:bg-yellow-400"} disabled:opacity-50`}
          data-testid="btn-guardar-config">
          <Save className="w-4 h-4" /> {saving ? "Guardando..." : saved ? "Guardado" : "Guardar cambios"}
        </button>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

      {/* Reconciliation windows */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <h3 className="font-semibold text-slate-900">Ventanas de conciliacion (dias habiles)</h3>
        <p className="text-xs text-slate-500">Cuantos dias despues del corte se espera que aparezca el deposito en banco/procesador.</p>
        <div className="grid grid-cols-2 gap-4">
          {CHANNELS.map(ch => (
            <div key={ch.key}>
              <label className="block text-sm font-medium text-slate-700 mb-1">{ch.label}</label>
              <input type="number" min={1} max={30}
                value={config.reconciliation_windows?.[ch.key] || 0}
                onChange={e => set(`reconciliation_windows.${ch.key}`, parseInt(e.target.value) || 0)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
          ))}
        </div>
      </div>

      {/* Minimo de caja (cash flow) */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="ops-config-saldo-minimo">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <Banknote className="w-4 h-4 text-yellow-500" /> Minimo de caja (flujo de caja semanal)
        </h3>
        <p className="text-xs text-slate-500">Debajo de este saldo proyectado, la sucursal se marca "Critico" y dispara una alerta. Se puede definir un minimo por sucursal; si no se define, se usa el default.</p>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Minimo default ($)</label>
          <input type="number" min={0} step={1000}
            value={config.saldo_minimo_default ?? 200000}
            onChange={e => set("saldo_minimo_default", parseFloat(e.target.value) || 0)}
            className="w-full max-w-xs border border-slate-200 rounded-lg px-3 py-2 text-sm"
            data-testid="input-saldo-minimo-default" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          {branches.map(b => (
            <div key={b.id}>
              <label className="block text-sm font-medium text-slate-700 mb-1">{b.name}</label>
              <input type="number" min={0} step={1000}
                placeholder={`Default: ${config.saldo_minimo_default ?? 200000}`}
                value={config.saldo_minimo_por_sucursal?.[b.id] ?? ""}
                onChange={e => set(`saldo_minimo_por_sucursal.${b.id}`, e.target.value === "" ? undefined : parseFloat(e.target.value) || 0)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                data-testid={`input-saldo-minimo-${b.id}`} />
            </div>
          ))}
        </div>
      </div>

      {/* Thresholds */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <h3 className="font-semibold text-slate-900">Umbrales de diferencia (%)</h3>
        <p className="text-xs text-slate-500">Define cuando una conciliacion se marca como OK, Alerta o Critico.</p>
        <div className="grid grid-cols-3 gap-4">
          {[
            { key: "ok_pct", label: "OK (diferencia ≤ X%)", color: "border-green-200 bg-green-50" },
            { key: "alerta_pct", label: "Alerta (diferencia ≤ X%)", color: "border-yellow-200 bg-yellow-50" },
            { key: "critico_pct", label: "Critico (diferencia > X%)", color: "border-red-200 bg-red-50" },
          ].map(({ key, label, color }) => (
            <div key={key} className={`border rounded-xl p-3 ${color}`}>
              <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
              <input type="number" min={0} max={100} step={0.1}
                value={config.reconciliation_thresholds?.[key] || 0}
                onChange={e => set(`reconciliation_thresholds.${key}`, parseFloat(e.target.value) || 0)}
                className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white" />
            </div>
          ))}
        </div>
      </div>

      {/* Alert anticipation */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <h3 className="font-semibold text-slate-900">Anticipacion de alertas de vigencias (dias)</h3>
        <p className="text-xs text-slate-500">Con cuantos dias de anticipacion se envian alertas antes del vencimiento.</p>
        <div className="flex gap-3">
          {(config.alert_anticipation_days || [60, 30, 7]).map((days, i) => (
            <input key={i} type="number" min={1} max={365} value={days}
              onChange={e => {
                const newArr = [...(config.alert_anticipation_days || [])];
                newArr[i] = parseInt(e.target.value) || days;
                set("alert_anticipation_days", newArr);
              }}
              className="w-24 border border-slate-200 rounded-lg px-3 py-2 text-sm text-center" />
          ))}
        </div>
      </div>

      {/* Viaticos policy */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <h3 className="font-semibold text-slate-900">Politica de viaticos</h3>
        <div className="grid grid-cols-2 gap-4">
          {[
            { key: "max_per_day_comida", label: "Maximo comida / dia ($)" },
            { key: "max_per_day_transporte", label: "Maximo transporte ($)" },
            { key: "max_per_day_hospedaje", label: "Maximo hospedaje / dia ($)" },
            { key: "max_per_day_otros", label: "Maximo otros ($)" },
            { key: "approver_threshold_corporativo", label: "Umbral aprobacion corp. admin ($)" },
            { key: "rendition_deadline_days", label: "Plazo rendicion (dias desde regreso)" },
          ].map(({ key, label }) => (
            <div key={key}>
              <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
              <input type="number" min={0}
                value={config.viaticos_policy?.[key] || 0}
                onChange={e => set(`viaticos_policy.${key}`, parseFloat(e.target.value) || 0)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
          ))}
        </div>
      </div>

      {/* Alert channels */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <h3 className="font-semibold text-slate-900">Canales de alertas</h3>
        {[["push", "Push (notificaciones navegador)"], ["email", "Email (via Resend)"]].map(([key, label]) => (
          <label key={key} className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={config.alert_channels?.[key] || false}
              onChange={e => set(`alert_channels.${key}`, e.target.checked)}
              className="w-4 h-4 accent-yellow-400" />
            <span className="text-sm font-medium text-slate-700">{label}</span>
          </label>
        ))}
      </div>

      {/* Videos de capacitación por rol (Módulo 10) */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="ops-config-training-videos">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <Video className="w-4 h-4 text-yellow-500" /> Videos de capacitación por rol
        </h3>
        <p className="text-xs text-slate-500">URL del video de capacitación (YouTube, Drive, etc.) que verá cada rol desde el botón de ayuda (?) y el tutorial de bienvenida.</p>
        <div className="space-y-3">
          {TRAINING_VIDEO_ROLES.map(r => (
            <div key={r.key}>
              <label className="block text-sm font-medium text-slate-700 mb-1">{r.label}</label>
              <input type="url" placeholder="https://..."
                value={config.training_video_urls?.[r.key] || ""}
                onChange={e => set(`training_video_urls.${r.key}`, e.target.value || undefined)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                data-testid={`input-training-video-${r.key}`} />
            </div>
          ))}
        </div>
      </div>
      {/* Wansoft (POS) */}
      {isCorpAdmin && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="ops-config-wansoft">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Wifi className="w-4 h-4 text-yellow-500" /> Wansoft (POS)
          </h3>
          <p className="text-xs text-slate-500">
            Integracion de solo lectura con el POS Wansoft (ventas). Estado de la cookie .ASPXAUTH,
            reinyeccion manual y ultimas corridas de sincronizacion.
          </p>

          {wansoftStatus && (
            <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg bg-slate-50" data-testid="wansoft-status-panel">
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                  wansoftStatus.status === "ok" ? "bg-green-100 text-green-700"
                    : wansoftStatus.status === "expired" ? "bg-red-100 text-red-700"
                    : "bg-slate-200 text-slate-600"
                }`}
                data-testid="wansoft-status-badge"
              >
                <span className={`w-2 h-2 rounded-full ${
                  wansoftStatus.status === "ok" ? "bg-green-500" : wansoftStatus.status === "expired" ? "bg-red-500" : "bg-slate-400"
                }`} />
                {wansoftStatus.status === "ok" ? "Sesion activa" : wansoftStatus.status === "expired" ? "Sesion caida" : "Sin cookie"}
              </span>
              <span className="text-xs text-slate-500">Ultimo OK: {wansoftStatus.last_ok_at ? new Date(wansoftStatus.last_ok_at).toLocaleString("es-MX") : "-"}</span>
              <span className="text-xs text-slate-500">ASP.NET_SessionId: {wansoftStatus.has_session_id ? "si" : "no"}</span>
              <span className="text-xs text-slate-500">Fallas seguidas: {wansoftStatus.consecutive_failures || 0}</span>
              {wansoftStatus.last_sync_note && (
                <span className="text-xs text-slate-600 w-full" data-testid="wansoft-last-sync-note">
                  Ultimo sync: {wansoftStatus.last_sync_note} ({wansoftStatus.last_sync_status || "-"}
                  {wansoftStatus.last_sync_at ? `, ${new Date(wansoftStatus.last_sync_at).toLocaleString("es-MX")}` : ""})
                </span>
              )}
              {wansoftStatus.status === "expired" && wansoftStatus.last_down_reason && (
                <span className="text-xs text-red-600 w-full flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Motivo de caida: {wansoftStatus.last_down_reason}
                  {wansoftStatus.next_attempt_at ? ` — proximo intento automatico: ${new Date(wansoftStatus.next_attempt_at).toLocaleString("es-MX")}` : ""}
                </span>
              )}
            </div>
          )}

          {wansoftError && <div className="p-2 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs" data-testid="wansoft-error">{wansoftError}</div>}
          {wansoftMsg && <div className="p-2 bg-green-50 border border-green-200 rounded-lg text-green-700 text-xs" data-testid="wansoft-msg">{wansoftMsg}</div>}

          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="password"
              placeholder="Pegar cookie .ASPXAUTH del navegador"
              value={wansoftCookie}
              onChange={e => setWansoftCookie(e.target.value)}
              className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm"
              data-testid="input-wansoft-aspxauth"
            />
            <button onClick={handleSaveWansoftCookie} disabled={wansoftSaving || !wansoftCookie.trim()}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
              data-testid="btn-wansoft-save-cookie">
              {wansoftSaving ? "Guardando..." : "Guardar cookie"}
            </button>
          </div>

          <button
            type="button"
            onClick={() => setWansoftHelpOpen(v => !v)}
            className="text-xs text-slate-500 underline hover:text-slate-700"
            data-testid="btn-wansoft-help-toggle"
          >
            {wansoftHelpOpen ? "Ocultar ayuda" : "¿Cómo consigo la cookie .ASPXAUTH?"}
          </button>
          {wansoftHelpOpen && (
            <ol className="text-xs text-slate-500 list-decimal pl-4 space-y-1 bg-slate-50 rounded-lg p-3" data-testid="wansoft-help-text">
              <li>Entrá a Wansoft en el navegador y logueate normal.</li>
              <li>Apretá F12 para abrir las herramientas de desarrollador.</li>
              <li>Vas a la pestaña "Application" (o "Almacenamiento").</li>
              <li>En el panel izquierdo, abrí "Cookies" y elegí el sitio de Wansoft.</li>
              <li>Buscá la fila <code className="bg-slate-200 px-1 rounded">.ASPXAUTH</code> y copiá su "Value".</li>
              <li>Pegalo en el campo de arriba y apretá "Guardar cookie".</li>
            </ol>
          )}

          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-2 pt-2 border-t border-slate-100">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Desde (opcional)</label>
              <input type="date" value={wansoftFrom} onChange={e => setWansoftFrom(e.target.value)}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="input-wansoft-from-date" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Hasta (opcional)</label>
              <input type="date" value={wansoftTo} onChange={e => setWansoftTo(e.target.value)}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="input-wansoft-to-date" />
            </div>
            <button onClick={handleWansoftSync} disabled={wansoftSyncing}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
              data-testid="btn-wansoft-sync-now">
              <RefreshCw className={`w-4 h-4 ${wansoftSyncing ? "animate-spin" : ""}`} />
              {wansoftSyncing ? "Sincronizando..." : "Sincronizar ahora"}
            </button>
          </div>
          <p className="text-xs text-slate-400">Sin fechas, sincroniza automaticamente los dias faltantes de los ultimos 30.</p>

          <div>
            <h4 className="text-sm font-medium text-slate-700 mb-2">Ultimas corridas</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="wansoft-runs-table">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-200">
                    <th className="py-1.5 pr-3">Inicio</th>
                    <th className="py-1.5 pr-3">Disparado por</th>
                    <th className="py-1.5 pr-3">Estado</th>
                    <th className="py-1.5 pr-3">Dias guardados</th>
                    <th className="py-1.5 pr-3">Sin acceso</th>
                  </tr>
                </thead>
                <tbody>
                  {wansoftRuns.length === 0 ? (
                    <tr><td colSpan={5} className="py-3 text-slate-400 text-center">Sin corridas todavia</td></tr>
                  ) : wansoftRuns.map(r => (
                    <tr key={r.id} className="border-b border-slate-100" data-testid={`wansoft-run-row-${r.id}`}>
                      <td className="py-1.5 pr-3">{r.iniciado_at ? new Date(r.iniciado_at).toLocaleString("es-MX") : "-"}</td>
                      <td className="py-1.5 pr-3">{r.disparado_por}</td>
                      <td className="py-1.5 pr-3">
                        <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${
                          r.estado === "ok" ? "bg-green-100 text-green-700" : r.estado === "error" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"
                        }`}>{r.estado}</span>
                      </td>
                      <td className="py-1.5 pr-3">{r.summary?.dias_guardados ?? "-"}/{r.summary?.dias_procesados ?? "-"}</td>
                      <td className="py-1.5 pr-3">{(r.summary?.sucursales_sin_acceso || []).join(", ") || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
